// Promotions & Marketing Campaigns Types

// ===== ENUMS =====

export type PromotionType = 'percentage' | 'fixed_amount' | 'buy_x_get_y' | 'bundle';
export type PromotionStatus = 'active' | 'expired' | 'scheduled' | 'disabled';
export type CampaignType = 'email' | 'sms' | 'push' | 'all';
export type CampaignTargetAudience =
  | 'all_clients'
  | 'inactive_clients'
  | 'top_spenders'
  | 'recent_clients'
  | 'new_clients'
  | 'membership_subscribers'
  | 'custom';
export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'sending'
  | 'sent'
  | 'partially_failed'
  | 'failed'
  | 'cancelled';
export type RecipientStatus =
  | 'pending'
  | 'delivered'
  | 'opened'
  | 'clicked'
  | 'bounced'
  | 'failed'
  | 'unsubscribed';

// ===== PROMOTION DTOs =====

export interface PromotionDto {
  id: number;
  barberId: number;
  barberName: string;

  code: string;
  name: string;
  description?: string;

  promotionType: PromotionType;
  promotionTypeDisplay: string;
  discountValue: number;
  discountDisplay: string;

  // Buy X Get Y specific
  buyQuantity?: number;
  getQuantity?: number;

  // Validity
  startDate: string;
  endDate?: string;
  isActive: boolean;
  status: PromotionStatus;
  statusDisplay: string;

  // Usage limits
  maxRedemptions?: number;
  currentRedemptions: number;
  maxPerUser: number;
  remainingRedemptions?: number;

  // Restrictions
  minimumOrderAmount?: number;
  firstTimeCustomersOnly: boolean;

  // Targeting
  applicableServiceIds: number[];
  applicableBundleIds: number[];
  applicableServiceNames: string[];
  applicableBundleNames: string[];

  // Display
  isFeatured: boolean;
  termsAndConditions?: string;

  // Analytics
  totalRevenue: number;
  totalDiscount: number;

  createdAt: string;
  updatedAt?: string;
}

export interface PromotionSummaryDto {
  id: number;
  code: string;
  name: string;
  discountDisplay: string;
  status: PromotionStatus;
  statusDisplay: string;
  currentRedemptions: number;
  maxRedemptions?: number;
  endDate?: string;
  isFeatured: boolean;
}

export interface CreatePromotionDto {
  code: string;
  name: string;
  description?: string;
  promotionType: PromotionType;
  discountValue: number;
  buyQuantity?: number;
  getQuantity?: number;
  startDate?: string;
  endDate?: string;
  maxRedemptions?: number;
  maxPerUser?: number;
  minimumOrderAmount?: number;
  firstTimeCustomersOnly?: boolean;
  applicableServiceIds?: number[];
  applicableBundleIds?: number[];
  isFeatured?: boolean;
  termsAndConditions?: string;
}

export interface UpdatePromotionDto {
  name?: string;
  description?: string;
  discountValue?: number;
  buyQuantity?: number;
  getQuantity?: number;
  startDate?: string;
  endDate?: string;
  maxRedemptions?: number;
  maxPerUser?: number;
  minimumOrderAmount?: number;
  firstTimeCustomersOnly?: boolean;
  applicableServiceIds?: number[];
  applicableBundleIds?: number[];
  isFeatured?: boolean;
  termsAndConditions?: string;
  isActive?: boolean;
}

export interface PromotionQueryParams {
  page?: number;
  pageSize?: number;
  status?: PromotionStatus;
  searchTerm?: string;
  includeExpired?: boolean;
  sortBy?: string;
  sortDescending?: boolean;
}

// ===== VALIDATION DTOs =====

export interface ValidatePromotionDto {
  code: string;
  barberId: number;
  userId?: string;
  subtotal?: number;
  serviceIds?: number[];
  bundleIds?: number[];
}

export interface PromotionValidationResult {
  isValid: boolean;
  promotionId?: number;
  promotionName?: string;
  discountAmount?: number;
  discountDisplay?: string;
  errorCode?: string;
  errorMessage?: string;
}

// ===== ANALYTICS DTOs =====

export interface PromotionPerformanceDto {
  promotionId: number;
  promotionName: string;
  code: string;

  // Usage metrics
  totalRedemptions: number;
  uniqueCustomers: number;
  repeatRedemptions: number;

  // Financial metrics
  totalRevenue: number;
  totalDiscount: number;
  averageOrderValue: number;
  averageDiscount: number;

  // Conversion metrics
  viewCount: number;
  conversionRate: number;

  // Time metrics
  daysSinceCreated: number;
  peakRedemptionDay?: string;
  averageRedemptionsPerDay: number;
}

export interface TopPromotionDto {
  promotionId: number;
  code: string;
  name: string;
  redemptionCount: number;
  totalRevenue: number;
  totalDiscount: number;
}

export interface DailyRedemptionDto {
  date: string;
  redemptions: number;
  revenue: number;
  discount: number;
}

export interface PromotionsSummaryDto {
  totalPromotions: number;
  activePromotions: number;
  totalRedemptions: number;
  totalDiscountGiven: number;
  totalRevenueWithPromotions: number;
  averageOrderValue: number;
  topPromotions: TopPromotionDto[];
}

// ===== CAMPAIGN DTOs =====

export interface CampaignDto {
  id: number;
  promotionId: number;
  promotion: PromotionSummaryDto;

  name: string;
  type: CampaignType;
  typeDisplay: string;
  targetAudience: CampaignTargetAudience;
  audienceDisplay: string;

  scheduledAt?: string;
  sentAt?: string;
  status: CampaignStatus;
  statusDisplay: string;

  subject: string;
  messageBody: string;

  // Metrics
  totalRecipients: number;
  deliveredCount: number;
  openedCount: number;
  clickedCount: number;
  redeemedCount: number;

  // Computed rates
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  redemptionRate: number;

  createdAt: string;
  updatedAt?: string;
}

export interface CampaignSummaryDto {
  id: number;
  name: string;
  promotionName: string;
  promotionCode: string;
  status: CampaignStatus;
  statusDisplay: string;
  totalRecipients: number;
  redeemedCount: number;
  sentAt?: string;
  createdAt: string;
}

export interface CreateCampaignDto {
  promotionId: number;
  name: string;
  type: CampaignType;
  targetAudience: CampaignTargetAudience;
  audienceFilters?: AudienceFiltersDto;
  subject: string;
  messageBody: string;
  scheduledAt?: string;
  customRecipientIds?: string[];
}

export interface UpdateCampaignDto {
  name?: string;
  type?: CampaignType;
  targetAudience?: CampaignTargetAudience;
  audienceFilters?: AudienceFiltersDto;
  subject?: string;
  messageBody?: string;
}

export interface AudienceFiltersDto {
  inactiveDays?: number;
  topPercentage?: number;
  recentDays?: number;
}

export interface ScheduleCampaignDto {
  scheduledAt: string;
}

export interface CampaignQueryParams {
  page?: number;
  pageSize?: number;
  status?: CampaignStatus;
  promotionId?: number;
  searchTerm?: string;
  sortBy?: string;
  sortDescending?: boolean;
}

// ===== AUDIENCE DTOs =====

export interface AudiencePreviewDto {
  totalCount: number;
  sampleRecipients: AudienceRecipientDto[];
}

export interface AudienceRecipientDto {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  lastBookingDate?: string;
  totalSpent?: number;
}

// ===== ANALYTICS DTOs =====

export interface CampaignAnalyticsDto {
  campaignId: number;
  campaignName: string;
  promotionCode: string;

  // Delivery metrics
  totalSent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  failed: number;

  // Conversion metrics
  redeemed: number;
  revenueGenerated: number;
  discountGiven: number;

  // Computed rates
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  redemptionRate: number;
  roi: number;

  // Redemptions by day
  redemptionsByDay: DailyRedemptionDto[];
}

// ===== TEMPLATE PLACEHOLDERS =====

export const MESSAGE_TEMPLATE_PLACEHOLDERS: Record<string, string> = {
  '{{FirstName}}': "Customer's first name",
  '{{LastName}}': "Customer's last name",
  '{{PromoCode}}': 'The promotion code',
  '{{DiscountValue}}': "Formatted discount (e.g., '20%' or '$10')",
  '{{ExpiryDate}}': 'Promotion expiration date',
  '{{PromotionName}}': 'Name of the promotion',
  '{{BarberName}}': "Barber's display name",
};

// ===== PAGED RESULT =====

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}
